import { AIEntityRefreshDetail, AI_ENTITY_REFRESH, HIDE_PREFIXES, MODULE_MAP } from '@/config/aiModuleMap';
import { LOCAL_SUGGESTIONS } from '@/config/aiSuggestions';
import FloatingAI from '@/core/aiFloating';
import { useAIContextStore } from '@/core/aiFloating/contextStore';
import { setAiRuntime } from '@/core/runtime';
import type { EntityAction } from '@/core/services/aiAssistant';
import { _getAuthInfo } from '@/services/user';
import { fileRequest, request } from '@/utils/request';
import { tokenUtil } from '@/utils/token';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, message } from 'antd';
import { ReactNode, useEffect } from 'react';
import 'tailwindcss/tailwind.css';
import { history } from 'umi';
import './i18n.js';
/**
 * antd国际化资源
 */
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import zhCN from 'antd/locale/zh_CN';
/**
 * dayjs国际化
 */
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import { theme } from './theme';
import { visitPoint } from './utils';

const queryClient = new QueryClient();
const locale = { zh: zhCN, en: enUS, ja: jaJP }[window.lang || 'zh'];

/**
 * 消费 pollWithStatus 消息里的 entityAction 信号：
 * - 目前仅识别 action === 'refresh'；其它值静默忽略；
 * - entityType === 'experiment'：同实验触发 react-query 失效，异实验/无 id 走跳转；
 * - entityType === 'activity'：同活动派发 AI_ENTITY_REFRESH 事件（flow 页监听重拉），异活动走跳转；
 * - 不弹任何 message / modal，反馈依赖 AI type=3 回复文案。
 */
function handleEntityRefresh(action: EntityAction) {
  if (action.action !== 'refresh') return;
  switch (action.entityType) {
    case 'experiment': {
      const entityId = action.entityId;
      if (!entityId) return;
      const curPath = window.location.pathname;
      const curId = new URLSearchParams(window.location.search).get('id');
      // 全路由匹配：兼容 /experiment/expEdit 与 /abtest/experiment/expEdit
      const onExpEdit = curPath.endsWith('/experiment/expEdit');
      if (onExpEdit && curId === entityId) {
        queryClient.invalidateQueries({ queryKey: ['/exp/getExpInfo', entityId] });
      } else {
        history.push(`/experiment/expEdit?id=${entityId}`);
      }
      return;
    }
    case 'activity': {
      const entityId = action.entityId;
      if (!entityId) return;
      const curPath = window.location.pathname;
      // 路由：/smart/activity/flow/:id，id 为路径参数
      const onFlowEdit = curPath.includes('/activity/flow/');
      const curId = curPath.split('/activity/flow/')[1]?.split('/')[0];
      if (onFlowEdit && curId === entityId) {
        window.dispatchEvent(
          new CustomEvent<AIEntityRefreshDetail>(AI_ENTITY_REFRESH, {
            detail: { entityType: 'activity', entityId }
          })
        );
      } else {
        history.push(`/activity/flow/${entityId}`);
      }
      return;
    }
    default:
      // 未识别的 entityType：静默忽略，预留后续扩展
      return;
  }
}

setAiRuntime({
  request,
  fileRequest,
  getToken: () => tokenUtil.get(),
  apiBase: '/promeApi',
  appBase: '/smart',
  navigate: (path) => history.push(path),
  subscribeRoute: (cb) => {
    const un = history.listen(({ location }) => cb(location.pathname));
    return () => un?.();
  },
  moduleMap: MODULE_MAP,
  hidePrefixes: HIDE_PREFIXES,
  localSuggestions: LOCAL_SUGGESTIONS,
  getGameId: () => useAIContextStore.getState().ctx.gameId,
  onEntityRefresh: handleEntityRefresh,
  getAuthKeys: () => _getAuthInfo() as Promise<string[]>
});

const DEFAULT_TITLE = '有策 | 智能运营';

function getTitle(pathname: string = window.location.pathname) {
  if (pathname.includes('/widget')) return '有策 | 桌面小组件';
  if (pathname.includes('/manage')) return '有策 | 智能运营';
  return DEFAULT_TITLE;
}

function applyTitle() {
  const title = getTitle();
  if (document.title !== title) {
    document.title = title;
  }
}

function TitleHolder({ children }: { children: ReactNode }) {
  useEffect(() => {
    applyTitle();

    // 1) URL 变化时重算（覆盖 widget 内部自身路由切换）
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const ret = origPush.apply(this, args);
      applyTitle();
      return ret;
    };
    window.history.replaceState = function (...args) {
      const ret = origReplace.apply(this, args);
      applyTitle();
      return ret;
    };
    window.addEventListener('popstate', applyTitle);

    // 2) 基座/外部改写 title 时实时回弹
    const titleEl = document.querySelector('title');
    const observer = titleEl ? new MutationObserver(applyTitle) : undefined;
    observer?.observe(titleEl as Node, { childList: true, characterData: true, subtree: true });

    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener('popstate', applyTitle);
      observer?.disconnect();
    };
  }, []);
  return <>{children}</>;
}
/**
 *
 */
//导出插件所需的qiankun对象以进行生命周期配置
export const qiankun = {
  // 应用加载之前
  async bootstrap(props: any) {
    if (props) {
      // console.log('prome-中台推荐系统应用加载', props);
      tokenUtil.set(props.token);
      props.window.microAppInfo = props.microAppInfo;
    }
  },
  // 应用 render 之前触发
  async mount() {
    applyTitle();
  },
  // 应用卸载之后触发
  async unmount() {
    message.destroy();
  }
};
export function render(oldRender: any) {
  //子应用控制可被加载渲染的环境范围
  /**
   * 线上资源仅支持被部分主站资源加载
   */
  // if (window.location.host.includes('netease.com')) {
  //   if (!window.isMainVisit) {
  //     return;
  //   }
  // }
  oldRender();
}

export function rootContainer(container: any) {
  return (
    <QueryClientProvider client={queryClient}>
      <TitleHolder>
        <ConfigProvider locale={locale} theme={theme}>
          {container}
          <FloatingAI />
        </ConfigProvider>
      </TitleHolder>
    </QueryClientProvider>
  );
}

export function onRouteChange({ location, clientRoutes, routes, action, basename, isFirst }) {
  applyTitle();
  visitPoint({
    ...location
  });
}
